// Budget Sheet — mirrors the "Budget Guideline" Excel provided by the user.
// Layout:
//   1) Top P&L Guidelines table — Row × B.E.P. Sales is entered manually;
//      RM / Inhouse / Outsource / FC / Mktg / Pkg auto-compute from Sales
//      using fixed percentages. Categories mapped to each cost centre in
//      the Category Master appear as sub-rows under that cost centre.
//   2) Inhouse Cost breakdown     (Wages, Electricity, Maintenance, …)
//   3) Outsource Cost breakdown   (Cutting, Machining, Plating, …)
//   4) Cost-Center Monthly Allocation table (Apr..Mar × Cost Centre)
//        → drives the 75%/100% Purchase Dashboard indicators and Indent-approval logic.

import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Save, Plus, Trash2, RotateCcw, ClipboardList } from "lucide-react";

const currentFY = () => {
  const now = new Date();
  const y = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  return `${y}-${String(y + 1).slice(-2)}`;
};
const fyOpts = () => {
  const cur = currentFY();
  const start = parseInt(cur.split("-")[0], 10);
  const opts = [];
  for (let i = 2; i >= -3; i--) {
    const y = start + i;
    opts.push(`${y}-${String(y + 1).slice(-2)}`);
  }
  return opts;
};
const MONTHS = ["Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec", "Jan", "Feb", "Mar"];
const fmtINR = (n) => Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Sales-driven percentages for the six auto-computed cost-centre rows.
const CC_PCT = {
  rm: 0.50,
  inhouse: 0.12,
  outsource: 0.08,
  fc: 0.18,
  mkt: 0.02,
  pkg: 0.015,
};

// Fuzzy keywords for matching Category-Master `cost_center` values back to
// the six budget rows. Robust to naming variations (e.g. "In-house Cost"
// vs "Inhouse Cost", "Outsourcing" vs "Outsource").
const CC_KEYWORDS = {
  rm:        ["raw", "material"],
  inhouse:   ["inhouse", "in-house", "in house"],
  outsource: ["outsourc"],
  fc:        ["fixed"],
  mkt:       ["market"],
  pkg:       ["packag", "transport"],
};
const matchCcKey = (ccName) => {
  const s = (ccName || "").toLowerCase();
  for (const [k, kws] of Object.entries(CC_KEYWORDS)) {
    if (kws.some((w) => s.includes(w))) return k;
  }
  return null;
};

// -------- default rows matching the user's Excel layout --------
// Only `bep` is persisted now — target/actual/% columns were dropped per
// the 2026-02-20 spec ("remove Target 1/2/3, Q1 Actual, % columns").
const defaultPnlRows = () => [
  { key: "sales",     label: "Sales",                          bep: 250 },
  { key: "rm",        label: "Raw Material -50%",              bep: 0, computed: true },
  { key: "inhouse",   label: "Inhouse cost -12%",              bep: 0, computed: true },
  { key: "outsource", label: "Outsource cost -8%",             bep: 0, computed: true },
  { key: "fc",        label: "Fixed cost -18%",                bep: 0, computed: true },
  { key: "mkt",       label: "Marketing cost -2%",             bep: 0, computed: true },
  { key: "pkg",       label: "Packaging & transport -1.5%",    bep: 0, computed: true },
  { key: "total",     label: "Total mfg cost",                 bep: 0, computed: true },
  { key: "profit",    label: "Profitability",                  bep: 0, computed: true },
  { key: "margin",    label: "Profit margin",                  bep: 0, computed: true },
];
const defaultInhouseRows = () => [
  "Wages", "Electricity", "Maintenance", "Pr Consumable", "Gr Consumable",
  "Tooling", "Fixture", "Guages", "Admin", "Testing", "Travelling Expn (MTKG)",
].map((label, i) => ({ key: `in_${i}`, label, bep: "" }));
const defaultOutsourceRows = () => [
  "Cutting", "Machining", "Plating", "Silver Plating", "Anodising",
  "Hardening", "Nickel Plating", "Zinc Plating", "Phosphating", "Forging",
].map((label, i) => ({ key: `out_${i}`, label, bep: "" }));

const numCell = (v) => (v === "" || v == null ? "" : Number(v));

// Small numeric input styled to look like a spreadsheet cell
const Cell = ({ value, onChange, testid, readOnly = false, className = "", align = "right" }) => (
  <input
    type={readOnly ? "text" : "number"}
    step="0.01"
    value={value ?? ""}
    onChange={onChange}
    data-testid={testid}
    readOnly={readOnly}
    className={`w-full px-2 py-1 text-xs font-mono border border-slate-200 rounded ${
      readOnly ? "bg-slate-50 text-slate-600" : "bg-white focus:outline-none focus:ring-1 focus:ring-[#1D2951]/30"
    } text-${align} ${className}`}
  />
);

// Given the current pnl rows, recompute the six sales-driven rows and the
// three trailing totals. Sales stays whatever the user typed.
const recomputeFromSales = (rows) => {
  const salesRow = rows.find((r) => r.key === "sales");
  const sales = Number(salesRow?.bep || 0);
  const derived = {
    rm: +(sales * CC_PCT.rm).toFixed(2),
    inhouse: +(sales * CC_PCT.inhouse).toFixed(2),
    outsource: +(sales * CC_PCT.outsource).toFixed(2),
    fc: +(sales * CC_PCT.fc).toFixed(2),
    mkt: +(sales * CC_PCT.mkt).toFixed(2),
    pkg: +(sales * CC_PCT.pkg).toFixed(2),
  };
  const total = +(derived.rm + derived.inhouse + derived.outsource + derived.fc + derived.mkt + derived.pkg).toFixed(2);
  const profit = +(sales - total).toFixed(2);
  const margin = sales > 0 ? +((profit / sales) * 100).toFixed(2) : 0;
  return rows.map((r) => {
    if (r.key === "sales") return r;
    if (r.key === "total")  return { ...r, bep: total };
    if (r.key === "profit") return { ...r, bep: profit };
    if (r.key === "margin") return { ...r, bep: margin };
    if (r.key in derived)   return { ...r, bep: derived[r.key] };
    return r;
  });
};

export default function BudgetSheet() {
  const [fy, setFy] = useState(currentFY());
  const [pnl, setPnl] = useState(() => recomputeFromSales(defaultPnlRows()));
  const [inhouse, setInhouse] = useState(defaultInhouseRows());
  const [outsource, setOutsource] = useState(defaultOutsourceRows());
  const [monthly, setMonthly] = useState({}); // { <cost_center>: [12 values] }
  const [ccList, setCcList] = useState([]);
  const [categories, setCategories] = useState([]); // [{ name, cost_center }]
  const [catBreakup, setCatBreakup] = useState({}); // { <category name>: <amount> }
  const [saving, setSaving] = useState(false);
  const [util, setUtil] = useState([]);       // utilization rows from backend
  // Live spending derived from GRNs + fixed-cost bills within the FY.
  const [catSpend, setCatSpend] = useState({});   // { <category name>: ₹ lakh }
  const [ccSpend, setCcSpend]   = useState({});   // { <cost_center name>: ₹ lakh }
  const [seeding, setSeeding] = useState(false);

  // Restore the default Inhouse / Outsource / Fixed cost breakdown mappings
  // in Category Master (non-destructive). Runs once per session automatically
  // so the sheet is never empty; user can re-run manually via the button.
  const seedDefaults = async ({ silent = false } = {}) => {
    setSeeding(true);
    try {
      const { data } = await api.post("/budget-sheet/seed-default-breakup");
      if (!silent) {
        const total = (data.cost_centers_created || 0) + (data.categories_created || 0) + (data.categories_mapped || 0);
        if (total > 0) {
          toast.success(`Restored default breakdown — ${data.categories_mapped || 0} mapped, ${data.categories_created || 0} created, ${data.cost_centers_created || 0} new cost centres`);
        } else {
          toast.info("Already up to date — nothing new to restore");
        }
      }
      await load();
    } catch (e) {
      if (!silent) toast.error(e?.response?.data?.detail || "Restore failed");
    } finally {
      setSeeding(false);
    }
  };

  const load = async () => {
    try {
      const [{ data: sheet }, { data: ccs }, { data: u }, { data: cats }, { data: spend }] = await Promise.all([
        api.get(`/budget-sheet?fy=${fy}`),
        api.get("/cost-centers").catch(() => ({ data: [] })),
        api.get(`/budget-sheet/utilization?fy=${fy}`).catch(() => ({ data: { rows: [] } })),
        api.get("/categories").catch(() => ({ data: [] })),
        api.get(`/budget-sheet/category-spend?fy=${fy}`).catch(() => ({ data: { per_category: {}, per_cost_center: {} } })),
      ]);
      setCcList((ccs || []).map((c) => c.name).filter(Boolean));
      setCategories((cats || []).map((c) => ({
        name: c.name || "",
        cost_center: (c.cost_center || "").trim(),
      })).filter((c) => c.name));
      // Merge persisted Sales into defaults, then let recompute derive the rest.
      const persisted = (sheet.pnl_targets && sheet.pnl_targets.length) ? sheet.pnl_targets : [];
      const salesBep = persisted.find((r) => r.key === "sales")?.bep;
      const base = defaultPnlRows().map((r) => (r.key === "sales" && salesBep != null && salesBep !== "")
        ? { ...r, bep: Number(salesBep) }
        : r);
      setPnl(recomputeFromSales(base));
      setInhouse(sheet.inhouse_breakdown && sheet.inhouse_breakdown.length ? sheet.inhouse_breakdown : defaultInhouseRows());
      setOutsource(sheet.outsource_breakdown && sheet.outsource_breakdown.length ? sheet.outsource_breakdown : defaultOutsourceRows());
      setMonthly(sheet.cost_center_monthly || {});
      setCatBreakup(sheet.category_breakup || {});
      setUtil(u.rows || []);
      setCatSpend(spend?.per_category || {});
      setCcSpend(spend?.per_cost_center || {});
    } catch (e) {
      toast.error("Failed to load Budget Sheet");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [fy]);

  // First-session auto-seed so the sheet is never empty after the Inhouse /
  // Outsource sub-tables were consolidated into the category sub-rows.
  useEffect(() => {
    const flag = "cpms_budget_default_seeded";
    if (!sessionStorage.getItem(flag)) {
      sessionStorage.setItem(flag, "1");
      seedDefaults({ silent: true });
    }
    /* eslint-disable-next-line */
  }, []);

  // Categories grouped by budget row-key (rm / inhouse / outsource / fc / mkt / pkg).
  const catsByRow = useMemo(() => {
    const map = { rm: [], inhouse: [], outsource: [], fc: [], mkt: [], pkg: [] };
    categories.forEach((c) => {
      const k = matchCcKey(c.cost_center);
      if (k && map[k]) map[k].push(c.name);
    });
    Object.keys(map).forEach((k) => map[k].sort((a, b) => a.localeCompare(b)));
    return map;
  }, [categories]);

  const save = async () => {
    setSaving(true);
    try {
      const clean = (rows) => rows.map((r) => ({ ...r, bep: numCell(r.bep) }));
      const cleanMonthly = {};
      Object.entries(monthly).forEach(([cc, arr]) => {
        cleanMonthly[cc] = (arr || []).map((v) => Number(v || 0));
      });
      await api.put("/budget-sheet", {
        fy,
        pnl_targets: clean(pnl),
        inhouse_breakdown: clean(inhouse),
        outsource_breakdown: clean(outsource),
        cost_center_monthly: cleanMonthly,
        category_breakup: Object.fromEntries(
          Object.entries(catBreakup).map(([k, v]) => [k, Number(v || 0)])
        ),
      });
      toast.success("Budget Sheet saved");
      const { data: u } = await api.get(`/budget-sheet/utilization?fy=${fy}`);
      setUtil(u.rows || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  // Sales edits cascade to the six percentage-driven rows + totals.
  const updateSales = (value) => {
    const next = pnl.map((r) => (r.key === "sales" ? { ...r, bep: value === "" ? "" : Number(value) } : r));
    setPnl(recomputeFromSales(next));
  };

  // Per-category breakup amount (₹ lakh) entered under a cost-centre row.
  const setBreakupFor = (catName, value) => {
    setCatBreakup((prev) => ({
      ...prev,
      [catName]: value === "" ? "" : Number(value),
    }));
  };

  // Sum of per-category breakup inputs under a given budget row-key.
  const breakupTotalFor = (rowKey) => {
    const cats = catsByRow[rowKey] || [];
    return cats.reduce((s, cn) => s + Number(catBreakup[cn] || 0), 0);
  };

  // Live spend (₹ lakh) for a given budget row-key. Falls back to summing
  // spending for whichever CC names in the master match this row's keyword
  // — so the number stays correct even if the user has multiple CCs mapped
  // to the same P&L bucket (e.g. "In-house Cost" + "Inhouse").
  const spendForRow = (rowKey) => {
    let total = 0;
    Object.entries(ccSpend || {}).forEach(([ccName, val]) => {
      if (matchCcKey(ccName) === rowKey) total += Number(val || 0);
    });
    return +total.toFixed(2);
  };

  const addCcAllocation = (cc) => {
    if (!cc || monthly[cc]) return;
    setMonthly({ ...monthly, [cc]: Array(12).fill(0) });
  };
  const removeCcAllocation = (cc) => {
    const next = { ...monthly }; delete next[cc]; setMonthly(next);
  };
  const setCcMonth = (cc, m, value) => {
    const arr = [...(monthly[cc] || Array(12).fill(0))];
    arr[m] = value === "" ? "" : Number(value);
    setMonthly({ ...monthly, [cc]: arr });
  };

  const availableCcToAdd = useMemo(
    () => ccList.filter((c) => !(c in monthly)),
    [ccList, monthly]
  );

  // -------- Budget Guideline table (renders categories as sub-rows) --------
  const renderPnlTable = () => (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
      <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 text-sm font-semibold text-slate-900">
        Budget Guideline — P&amp;L Targets (₹ lakh)
      </div>
      <div className="overflow-x-auto">
        <table className="text-xs" style={{ tableLayout: "fixed" }}>
          <colgroup>
            <col style={{ width: "300px" }} />
            <col style={{ width: "150px" }} />
            <col style={{ width: "170px" }} />
            <col style={{ width: "180px" }} />
            <col style={{ width: "180px" }} />
          </colgroup>
          <thead>
            <tr className="bg-slate-100">
              <th className="px-2 py-2 text-left">Row</th>
              <th className="px-2 py-2 text-right">B.E.P.</th>
              <th className="px-2 py-2 text-right">75% Allowance</th>
              <th className="px-2 py-2 text-right">Total of Cost Breakup</th>
              <th className="px-2 py-2 text-right">Live Budget Spending</th>
            </tr>
          </thead>
          <tbody>
            {pnl.map((r) => {
              const isSales = r.key === "sales";
              const readOnly = !!r.computed;   // every non-sales row is derived
              const subCats = catsByRow[r.key] || null;
              // 75% allowance is meaningful only for the six sales-driven cost-centre rows.
              const isCcRow = r.key in CC_PCT;
              const allowance75 = isCcRow ? +(Number(r.bep || 0) * 0.75).toFixed(2) : null;
              const breakupSum = isCcRow ? breakupTotalFor(r.key) : null;
              // Highlight when breakup total drifts from the derived B.E.P.
              const bep = Number(r.bep || 0);
              const drift = breakupSum != null && Math.abs(breakupSum - bep) > 0.01;
              // Live spending for the CC row (₹ lakh, live from GRNs).
              const liveSpend = isCcRow ? spendForRow(r.key) : null;
              const overBep = liveSpend != null && bep > 0 && liveSpend > bep;
              const over75  = liveSpend != null && allowance75 != null && !overBep && liveSpend > allowance75;
              const liveTone = overBep
                ? "!bg-rose-50 !text-rose-700 !border-rose-200"
                : over75
                ? "!bg-amber-50 !text-amber-800 !border-amber-200"
                : "!bg-emerald-50 !text-emerald-800 !border-emerald-200";
              return (
                <React.Fragment key={r.key}>
                  <tr className={`border-t border-slate-100 ${r.computed ? "bg-slate-50/70 font-semibold" : ""}`}>
                    <td className="px-2 py-1 text-slate-800 truncate">{r.label}</td>
                    <td className="px-1 py-1">
                      <Cell
                        value={r.bep}
                        onChange={(e) => isSales ? updateSales(e.target.value) : null}
                        readOnly={readOnly}
                        testid={`budget-pnl-${r.key}-bep`}
                      />
                    </td>
                    <td className="px-1 py-1">
                      {allowance75 != null ? (
                        <Cell value={allowance75} readOnly testid={`budget-pnl-${r.key}-allow75`} className="!bg-amber-50 !text-amber-800" />
                      ) : (
                        <div className="text-center text-slate-300 text-[11px]">—</div>
                      )}
                    </td>
                    <td className="px-1 py-1">
                      {breakupSum != null ? (
                        <Cell
                          value={breakupSum.toFixed(2)}
                          readOnly
                          testid={`budget-pnl-${r.key}-breakup-total`}
                          className={drift ? "!bg-rose-50 !text-rose-700 !border-rose-200" : "!bg-slate-50 !text-slate-700"}
                        />
                      ) : (
                        <div className="text-center text-slate-300 text-[11px]">—</div>
                      )}
                    </td>
                    <td className="px-1 py-1">
                      {liveSpend != null ? (
                        <Cell
                          value={liveSpend.toFixed(2)}
                          readOnly
                          testid={`budget-pnl-${r.key}-live-spend`}
                          className={liveTone}
                        />
                      ) : (
                        <div className="text-center text-slate-300 text-[11px]">—</div>
                      )}
                    </td>
                  </tr>
                  {subCats && subCats.length > 0 && subCats.map((cn) => {
                    const catLive = +Number(catSpend[cn] || 0).toFixed(2);
                    const catPlan = Number(catBreakup[cn] || 0);
                    const catOver = catPlan > 0 && catLive > catPlan;
                    const catTone = catOver
                      ? "!bg-rose-50 !text-rose-700 !border-rose-200"
                      : catLive > 0
                      ? "!bg-emerald-50 !text-emerald-800 !border-emerald-200"
                      : "!bg-slate-50 !text-slate-500";
                    return (
                      <tr key={`${r.key}::${cn}`} className="border-t border-slate-50 bg-white">
                        <td className="pl-8 pr-2 py-1 text-slate-500 text-[11px] truncate" data-testid={`budget-cat-${r.key}-${cn}`}>
                          <span className="text-slate-400 mr-1">↳</span>{cn}
                        </td>
                        <td className="px-1 py-1"></td>
                        <td className="px-1 py-1"></td>
                        <td className="px-1 py-1">
                          <Cell
                            value={catBreakup[cn] ?? ""}
                            onChange={(e) => setBreakupFor(cn, e.target.value)}
                            testid={`budget-cat-${cn}-breakup`}
                          />
                        </td>
                        <td className="px-1 py-1">
                          <Cell
                            value={catLive.toFixed(2)}
                            readOnly
                            testid={`budget-cat-${cn}-live-spend`}
                            className={catTone}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="px-4 py-2 text-[11px] text-slate-500 bg-slate-50 border-t border-slate-200">
        Enter <span className="font-semibold">Sales</span> — RM / Inhouse / Outsource / Fixed / Marketing / Packaging auto-compute
        from the fixed % (50 / 12 / 8 / 18 / 2 / 1.5). Categories under each cost centre come from Category Master;
        fill their <span className="font-semibold">breakup boxes</span> to plan the split — the sum shows in the &ldquo;Total of Cost Breakup&rdquo;
        column and turns <span className="font-semibold text-rose-700">red</span> when it drifts from the B.E.P.
        <span className="font-semibold text-emerald-700"> Live Budget Spending</span> flows in from GRN inwards + fixed-cost bills automatically —
        <span className="font-semibold text-amber-700"> amber</span> at 75%, <span className="font-semibold text-rose-700">red</span> when it exceeds the B.E.P.
      </div>
    </div>
  );

  // -------- Inhouse / Outsource breakdown was removed in favour of the
  // per-category input boxes rendered directly under each cost-centre row
  // in the Budget Guideline table above. `inhouse` / `outsource` state is
  // kept so that any legacy data on the document is preserved on Save. --------

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-slate-500">Finance</div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900 inline-flex items-center gap-2">
            <ClipboardList size={22} /> Budget Sheet
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Owner-only. Enter Sales to auto-compute cost-centre budgets; the values here drive the 75% / 100% Purchase Dashboard indicators.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-md px-3 py-1.5">
            <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">FY</span>
            <select value={fy} onChange={(e) => setFy(e.target.value)} data-testid="budget-fy" className="bg-transparent text-xs font-mono focus:outline-none">
              {fyOpts().map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
          <button
            onClick={() => seedDefaults()}
            disabled={seeding}
            data-testid="budget-restore-defaults-btn"
            title="Re-populate Category Master with the standard Inhouse / Outsource / Fixed cost breakdown. Existing mappings are preserved."
            className="bg-white border border-slate-300 hover:bg-slate-50 disabled:opacity-60 text-slate-700 text-sm font-semibold px-3 py-1.5 rounded-md inline-flex items-center gap-1.5"
          >
            <RotateCcw size={13} /> {seeding ? "Restoring…" : "Restore Defaults"}
          </button>
          <button
            onClick={save}
            disabled={saving}
            data-testid="budget-save-btn"
            className="bg-[#1D2951] hover:bg-[#2B3A67] disabled:opacity-60 text-white text-sm font-semibold px-4 py-1.5 rounded-md inline-flex items-center gap-1.5"
          >
            <Save size={13} /> {saving ? "Saving…" : "Save Sheet"}
          </button>
        </div>
      </div>

      {renderPnlTable()}

      {/* Monthly Cost-Centre Allocation — the actual driver of 75%/100% approval */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 text-sm font-semibold text-slate-900 flex items-center justify-between">
          <span>Monthly Cost-Center Allocation (₹) — drives 75% / 100% budget indicators</span>
          {availableCcToAdd.length > 0 && (
            <div className="inline-flex items-center gap-1">
              <select
                data-testid="budget-cc-add-select"
                onChange={(e) => { if (e.target.value) { addCcAllocation(e.target.value); e.target.value = ""; } }}
                defaultValue=""
                className="border border-slate-300 rounded px-2 py-1 text-xs"
              >
                <option value="">+ add cost centre…</option>
                {availableCcToAdd.map((c) => (<option key={c} value={c}>{c}</option>))}
              </select>
              <Plus size={12} className="text-slate-500" />
            </div>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-slate-100">
                <th className="px-2 py-2 text-left w-[180px]">Cost Center</th>
                {MONTHS.map((m) => <th key={m} className="px-2 py-2 text-right">{m}</th>)}
                <th className="px-2 py-2 text-right w-24 bg-emerald-50">Total</th>
                <th className="px-2 py-2 w-10"></th>
              </tr>
            </thead>
            <tbody>
              {Object.keys(monthly).length === 0 && (
                <tr><td colSpan={15} className="text-center py-6 text-slate-400 italic">
                  No cost-centre allocations yet — pick one from the dropdown above.
                </td></tr>
              )}
              {Object.entries(monthly).map(([cc, arr]) => {
                const total = (arr || []).reduce((s, v) => s + Number(v || 0), 0);
                return (
                  <tr key={cc} className="border-t border-slate-100">
                    <td className="px-2 py-1 font-semibold text-slate-800">{cc}</td>
                    {MONTHS.map((m, i) => (
                      <td key={m} className="px-1 py-1">
                        <Cell value={arr?.[i] ?? 0} onChange={(e) => setCcMonth(cc, i, e.target.value)} testid={`budget-cc-${cc.replace(/\s+/g, "_")}-m${i}`} />
                      </td>
                    ))}
                    <td className="px-2 py-1 text-right font-mono font-semibold bg-emerald-50/50">₹{fmtINR(total)}</td>
                    <td className="px-1 py-1 text-right">
                      <button onClick={() => removeCcAllocation(cc)} className="p-1 text-rose-500 hover:bg-rose-50 rounded" title="Remove this cost centre">
                        <Trash2 size={12} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Live utilization — how much has been spent against each budget */}
      {util.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 text-sm font-semibold text-slate-900">
            Live Utilization ({fy})
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-slate-100">
                  <th className="px-3 py-2 text-left">Cost Center</th>
                  <th className="px-3 py-2 text-right">Allocated</th>
                  <th className="px-3 py-2 text-right">Spent</th>
                  <th className="px-3 py-2 text-right">%</th>
                  <th className="px-3 py-2 text-center">Status</th>
                </tr>
              </thead>
              <tbody>
                {util.map((r) => {
                  const over75 = r.pct >= 75, over100 = r.pct >= 100;
                  const badge = over100 ? "bg-rose-100 text-rose-800 border-rose-200" : over75 ? "bg-amber-100 text-amber-800 border-amber-200" : "bg-emerald-100 text-emerald-800 border-emerald-200";
                  return (
                    <tr key={r.cost_center} className="border-t border-slate-100">
                      <td className="px-3 py-2 font-medium">{r.cost_center}</td>
                      <td className="px-3 py-2 text-right font-mono">₹{fmtINR(r.allocated)}</td>
                      <td className="px-3 py-2 text-right font-mono">₹{fmtINR(r.spent)}</td>
                      <td className="px-3 py-2 text-right font-mono font-semibold">{r.pct}%</td>
                      <td className="px-3 py-2 text-center">
                        <span className={`inline-block text-[10px] uppercase tracking-widest border rounded px-2 py-0.5 font-semibold ${badge}`}>
                          {over100 ? "Overspent" : over75 ? "≥ 75%" : "OK"}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
